import React, { useState, useCallback, useEffect } from 'react';
import { auth, googleProvider, appleProvider } from '../firebase-config';
import { signInWithPopup, signInWithEmailAndPassword } from 'firebase/auth'; // Removed getRedirectResult & signInWithRedirect
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { callApi } from '../api';
import Spinner from '../components/Spinner';
import logoImage from '../assets/logo.png';

// Helper function to convert Firebase errors to user-friendly messages
const getFriendlyErrorMessage = (error) => {
  const errorCode = error.code || error.message;
  switch (errorCode) {
    case 'auth/user-not-found':
      return 'No account found with this email address. Please check your email or sign up.';
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again.';
    case 'auth/invalid-credential':
        return 'Invalid email or password. Please check your credentials and try again.';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'Sign-in was cancelled. Please try again.';
    case 'auth/popup-blocked':
      return 'Pop-up was blocked by your browser. Please allow pop-ups and try again.';
    // Add other cases as needed
    default:
      return 'Something went wrong. Please try again or contact support if the problem persists.';
  }
};

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  const handlePostLogin = useCallback(async (user) => {
    if (!user) return;

    try {
      const token = await user.getIdToken();

      try {
        // Attempt to fetch the user's profile
        await callApi('/users/me', 'GET', null, token);
      } catch (err) {
        // If the profile doesn't exist (404), create it
        if (err.status === 404) {
          console.log('User profile not found, creating one...');
          const nameParts = (user.displayName || 'New User').split(' ');
          const firstName = nameParts[0];
          const lastName = nameParts.slice(1).join(' ') || firstName;

          await callApi('/create_user_profile', 'POST', {
            email: user.email,
            username: user.email,
            first_name: firstName,
            last_name: lastName,
          }, token);
        } else {
          // For other API errors, re-throw to be caught by the outer catch
          throw err;
        }
      }

      // Redirect after profile is confirmed or created
      const { state } = location;
      const inviteToken = state?.inviteToken;

      if (inviteToken) {
        navigate('/join-team', { state: { inviteToken }, replace: true });
      } else {
        // Always redirect to dashboard base URL after login to avoid permission issues
        navigate('/admin/dashboard', { replace: true });
      }

    } catch (apiError) {
      console.error('Failed to process login:', apiError);
      // Handle specific API errors
      if (apiError.status === 400) {
        setError('Account setup required. Please complete your profile or contact support.');
      } else {
        setError('An error occurred during login. Please try again.');
      }
      setLoading(false);
    }
  }, [navigate, location]);

  useEffect(() => {
    document.title = 'Sign In - Unfold';
    window.scrollTo(0, 0);
  }, []);

  const handleEmailPasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      await handlePostLogin(userCredential.user);
    } catch (err) {
      console.error('Email/password sign-in error:', err);
      setError(getFriendlyErrorMessage(err));
      setLoading(false);
    }
  };

  // SIMPLIFIED Google Sign-In: Use signInWithPopup for all devices.
  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    try {
      const userCredential = await signInWithPopup(auth, googleProvider);
      await handlePostLogin(userCredential.user);
    } catch (error) {
      console.error('Google sign-in error:', error);
      setError(getFriendlyErrorMessage(error));
      setLoading(false); // Only set loading to false on error
    }
  };

  const handleAppleSignIn = async () => {
    setError('');
    setLoading(true);
    try {
      const userCredential = await signInWithPopup(auth, appleProvider);
      await handlePostLogin(userCredential.user);
    } catch (error) {
      console.error('Apple sign-in error:', error);
      setError(getFriendlyErrorMessage(error));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 sm:p-6 lg:p-8">
      <div className="max-w-md w-full mx-auto bg-white shadow-sm border border-gray-200 rounded-xl p-8">
        <div className="text-center mb-8">
          <Link to="/" className="flex items-center justify-center space-x-2 mb-6">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
              <img src={logoImage} alt="Unfold Logo" className="w-6 h-6 object-contain" />
            </div>
            <span className="text-xl font-semibold text-gray-900">unfold</span>
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Welcome back</h1>
          <p className="text-gray-600">Sign in to access your dashboard</p>
        </div>

        {/* Error Message - moved before forms */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-6">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        <div className="space-y-6">
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={loading}
                className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg bg-white text-gray-700 font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="font-medium text-gray-700">Continue with Google</span>
              </button>

              <button
                type="button"
                onClick={handleAppleSignIn}
                disabled={loading}
                className="w-full flex items-center justify-center px-4 py-3 bg-black text-white font-medium rounded-lg hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09z"/>
                  <path d="M15.53 3.83c.893-1.09 1.479-2.58 1.309-4.08-1.297.052-2.867.865-3.8 1.95-.832.974-1.56 2.58-1.364 4.005 1.44.118 2.867-.706 3.855-1.875z"/>
                </svg>
                <span className="font-medium text-white">Continue with Apple</span>
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or continue with email</span>
                </div>
              </div>

              <form onSubmit={handleEmailPasswordSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-3 py-3 sm:px-4 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-base"
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                      Password
                    </label>
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      className="w-full px-3 py-3 sm:px-4 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-base"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        <span>Signing In...</span>
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </button>
                </form>

              <div className="text-center">
                <p className="text-sm text-gray-600">
                  Don't have an account?{' '}
                  <Link to="/register" className="font-medium text-blue-600 hover:text-blue-500">
                    Sign up
                  </Link>
                </p>
              </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;